import React, { useEffect } from 'react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import Image from 'next/image';

// Initialize ScrollTrigger once
gsap.registerPlugin(ScrollTrigger);

type Props = {
    title: string;
    className?: string;
};

const ContentVideoSection: React.FC<Props> = ({ className }) => {
    useEffect(() => {
        let tl: gsap.core.Timeline | null = null;

        const initGSAP = () => {
            tl = gsap.timeline({
                scrollTrigger: {
                    trigger: '.img-div',
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: 5,
                    pin: true,
                },
            });

            tl.from('.img', {
                width: '100vw',
                height: '100vh',
                rotate: '0deg',
                duration: 4,
                ease: 'power1.out',
            })
                .to(
                    '.img video',
                    {
                        border: '5px solid #DFE5EB',
                        borderRadius: '10px',
                        duration: 4,
                        ease: 'power1.out',
                    },
                    0,
                )
                .to(
                    '.from-left',
                    {
                        left: '32%',
                        opacity: 1,
                        duration: 4,
                        ease: 'power1.out',
                    },
                    0,
                )
                .to(
                    '.from-right',
                    {
                        right: '15%',
                        opacity: 1,
                        duration: 4,
                        ease: 'power1.out',
                    },
                    0,
                )
                .to(
                    '.from-top',
                    {
                        top: '32%',
                        opacity: 1,
                        duration: 4,
                        ease: 'power1.out',
                    },
                    0,
                )
                .to(
                    '.from-bottom',
                    {
                        bottom: '25%',
                        opacity: 1,
                        duration: 4,
                        ease: 'power1.out',
                    },
                    0,
                );
        };

        const destroyGSAP = () => {
            if (tl) {
                tl.kill();
                tl = null;
            }
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
        };

        const resizeHandler = () => {
            if (window.innerWidth >= 768) {
                if (!tl) {
                    initGSAP();
                }
            } else {
                destroyGSAP();
            }
        };

        if (window.innerWidth >= 768) {
            initGSAP();
        }

        window.addEventListener('resize', resizeHandler);

        return () => {
            destroyGSAP();
            window.removeEventListener('resize', resizeHandler);
        };
    }, []); // Ensure this effect runs only once on component mount

    useEffect(() => {
        const cursor = document.createElement('div');
        cursor.classList.add('cursor');

        // Add your SVG here
        cursor.innerHTML = `
   <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="100" height="100">
  <defs>
    <style>
      .cls-1 {
        fill: #1FAF5F;
      }
      .cls-2 {
        fill: #111;
      }
      .cls-3 {
        font-family: 'Proxima Nova', sans-serif;
        font-size: 24px;
        font-weight: 700;
      }
      @keyframes rotate {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
      .rotating-text {
        animation: rotate 10s linear infinite;
        transform-origin: center;
      }
      text {
        shape-rendering: geometricPrecision;
      }
    </style>
  </defs>
  <g>
    <path class="cls-1" d="M100,0C44.86,0,0,44.86,0,100s44.86,100,100,100,100-44.86,100-100S155.14,0,100,0Z"/>
    <path class="cls-2" d="M139.75,103.5l-58.33,37.5c-.68.44-1.47.66-2.25.66-.68,0-1.38-.17-1.99-.51-1.34-.73-2.17-2.13-2.17-3.66V62.5c0-1.53.83-2.93,2.17-3.66,1.32-.72,2.97-.68,4.25.15l58.33,37.5c1.19.76,1.91,2.09,1.91,3.5s-.72,2.74-1.91,3.5Z"/>
    <g class="rotating-text">
      <text class="cls-3" transform="translate(99.38 31.99) rotate(8.28)"><tspan x="0" y="0">V</tspan></text>
      <text class="cls-3" transform="translate(120.64 35.94) rotate(21.56)"><tspan x="0" y="0">I</tspan></text>
      <text class="cls-3" transform="translate(129.65 39.13) rotate(33.61)"><tspan x="0" y="0">E</tspan></text>
      <text class="cls-3" transform="translate(145.36 48.71) rotate(53.17)"><tspan x="0" y="0">W</tspan></text>
      <text class="cls-3" transform="translate(161.17 72.31) rotate(68.99)"><tspan x="0" y="0"> </tspan></text>
      <text class="cls-3" transform="translate(164.93 80.46) rotate(82.06)"><tspan x="0" y="0">V</tspan></text>
      <text class="cls-3" transform="translate(167.1 101.99) rotate(95.12)"><tspan x="0" y="0">I</tspan></text>
      <text class="cls-3" transform="translate(166.9 111.47) rotate(108.79)"><tspan x="0" y="0">D</tspan></text>
      <text class="cls-3" transform="translate(159.26 132.53) rotate(126.13)"><tspan x="0" y="0">E</tspan></text>
      <text class="cls-3" transform="translate(148.61 147.6) rotate(143.9)"><tspan x="0" y="0">O</tspan></text>
      <text class="cls-3" transform="translate(128.64 160.86) rotate(157.86)"><tspan x="0" y="0"> </tspan></text>
      <text class="cls-3" transform="translate(120.13 164.32) rotate(167.05)"><tspan x="0" y="0">•</tspan></text>
      <text class="cls-3" transform="translate(108.18 166.82) rotate(176.15)"><tspan x="0" y="0"> </tspan></text>
      <text class="cls-3" transform="translate(99.27 168.01) rotate(-170.97)"><tspan x="0" y="0">V</tspan></text>
      <text class="cls-3" transform="translate(78.06 163.79) rotate(-157.69)"><tspan x="0" y="0">I</tspan></text>
      <text class="cls-3" transform="translate(69.1 160.49) rotate(-145.66)"><tspan x="0" y="0">E</tspan></text>
      <text class="cls-3" transform="translate(53.5 150.69) rotate(-126.08)"><tspan x="0" y="0">W</tspan></text>
      <text class="cls-3" transform="translate(38 126.89) rotate(-110.27)"><tspan x="0" y="0"> </tspan></text>
      <text class="cls-3" transform="translate(34.35 118.69) rotate(-97.2)"><tspan x="0" y="0">V</tspan></text>
      <text class="cls-3" transform="translate(32.47 97.13) rotate(-84.13)"><tspan x="0" y="0">I</tspan></text>
      <text class="cls-3" transform="translate(32.78 87.66) rotate(-70.46)"><tspan x="0" y="0">D</tspan></text>
      <text class="cls-3" transform="translate(40.7 66.69) rotate(-53.12)"><tspan x="0" y="0">E</tspan></text>
      <text class="cls-3" transform="translate(51.53 51.78) rotate(-35.37)"><tspan x="0" y="0">O</tspan></text>
      <text class="cls-3" transform="translate(71.68 38.77) rotate(-21.39)"><tspan x="0" y="0"> </tspan></text>
      <text class="cls-3" transform="translate(80.22 35.42) rotate(-12.21)"><tspan x="0" y="0">•</tspan></text>
    </g>
  </g>
</svg>
`;

        const cursorText = document.createElement('div');
        cursorText.classList.add('cursor-text');
        cursor.appendChild(cursorText);
        document.body.appendChild(cursor);

        const updateCursor = (e: MouseEvent) => {
            cursor.style.left = `${e.clientX}px`;
            cursor.style.top = `${e.clientY}px`;
        };

        const handleMouseEnter = () => {
            cursor.classList.add('large');
            cursor.style.opacity = '1'; // Make cursor visible when entering the section
        };

        const handleMouseLeave = () => {
            cursor.classList.remove('large');
            cursor.style.opacity = '0'; // Hide cursor when leaving the section
        };

        const section = document.querySelector('.img');
        section?.addEventListener('mousemove', updateCursor as any);
        section?.addEventListener('mouseenter', handleMouseEnter);
        section?.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            section?.removeEventListener('mousemove', updateCursor as any);
            section?.removeEventListener('mouseenter', handleMouseEnter);
            section?.removeEventListener('mouseleave', handleMouseLeave);
            document.body.removeChild(cursor);
        };
    }, []);

    const handleClick = () => {
        const videoModal = document.getElementById('videoModal');
        const videoElement = videoModal?.querySelector('video');
        const cursor = document.querySelector('.cursor') as HTMLElement;
        const videoModalContent = videoModal?.querySelector('.video-modal-content') as HTMLDivElement;

        if (videoModal && videoModalContent) {
            videoModal.style.display = 'block';
            videoModal.style.opacity = '1'; // Ensure overlay is visible

            // Apply initial styles for the content
            videoModalContent.style.opacity = '0';
            videoModalContent.style.transform = ' translate(-50%, -50%) scale(0.5)';
            videoModalContent.style.transition = 'opacity 1s ease, transform 1s ease'; // Smooth transition for scaling and fading in

            // Trigger the content transition
            setTimeout(() => {
                videoModalContent.style.opacity = '1';
                videoModalContent.style.transform = ' translate(-50%, -50%) scale(1)';
            }, 10); // Short delay to ensure initial styles are applied

            document.body.style.overflow = 'hidden'; // Disable background scroll

            if (videoElement) {
                videoElement.play();
            }

            if (cursor) {
                cursor.style.width = '100px'; // Scale down cursor
                cursor.style.height = '100px'; // Scale down cursor
            }
        }
    };

    const handleCloseModal = () => {
        const videoModal = document.getElementById('videoModal');
        const videoElement = videoModal?.querySelector('video');
        const cursor = document.querySelector('.cursor') as HTMLElement;
        const videoModalContent = videoModal?.querySelector('.video-modal-content') as HTMLDivElement;

        if (videoModal && videoModalContent) {
            // Apply transition for closing
            videoModalContent.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
            videoModalContent.style.opacity = '0';
            videoModalContent.style.transform = ' translate(-50%, -50%) scale(0.5)';

            // Hide the overlay and reset its opacity immediately after the transition ends
            setTimeout(() => {
                videoModal.style.opacity = '0'; // Start fading out the overlay
                setTimeout(() => {
                    videoModal.style.display = 'none'; // Hide the overlay after fade-out
                    document.body.style.overflow = ''; // Enable background scroll

                    if (videoElement) {
                        videoElement.pause();
                    }

                    if (cursor) {
                        cursor.style.width = '100px'; // Reset cursor size
                        cursor.style.height = '100px'; // Reset cursor size
                    }
                }, 500); // Matches the transition duration
            }, 0);
        }
    };

    return (
        <section className={` ${className}`}>
            <div id="main">
                <div className="img-div" onClick={handleClick}>
                    <div className="img">
                        <video playsInline autoPlay muted loop>
                            <source src="img/technology/video-odoo.mp4" type="video/mp4" />
                        </video>
                    </div>
                    <div className="img-hidden">
                        <video playsInline autoPlay muted loop>
                            <source src="img/technology/video-odoo.mp4" type="video/mp4" />
                        </video>
                    </div>
                    <div className="hidden_header">
                        <p>Scale with Powerful Streamline and OdooERP Solutions</p>
                    </div>
                    <div className="text from-left">
                        <p>Scale with</p>
                    </div>
                    <div className="text from-right">
                        <p>Powerful</p>
                    </div>
                    <div className="text from-top">
                        <p>Streamline and</p>
                    </div>
                    <div className="text from-bottom">
                        <p>OdooERP Solutions</p>
                    </div>
                </div>
                <div className="vid-text">
                    <p>
                        Tired of juggling multiple systems and feeling like you’re always one step behind? Odoo brings your
                        business operations together in one seamless platform, providing you with a real-time view of your entire
                        company.
                    </p>
                    <p>
                        In today’s dynamic business landscape, embracing innovative solutions is key to staying ahead. At Moweb
                        Limited, we bring you next-generation Odoo ERP solutions designed to elevate your business to new heights
                        of efficiency and innovation. Our tailored services empower you to transform operations, drive growth, and
                        lead with innovation.
                    </p>
                </div>
            </div>

            <div id="videoModal" className="video-modal">
                <div className="video-modal-content">
                    <a href="#" onClick={handleCloseModal} className="close-modal">
                        <Image
                            src="img/odoo/close.svg"
                            width={24}
                            height={24}
                            alt="Close"
                            className="close-modal-icon"
                            onClick={handleCloseModal}
                        />
                    </a>
                    <video controls>
                        <source src="img/technology/video-odoo.mp4" type="video/mp4" />
                    </video>
                </div>
            </div>
        </section>
    );
};

export default ContentVideoSection;
